import React, { useState, useEffect, useMemo, useRef } from "react";
import DeliveryTable from "../components/deliveryTable";
import InventoryDisplay from "../components/InventoryDisplay";
import type {
  CommonPageProps,
  DeliverySchedule,
  InventoryGroup,
  UsageRecord,
  InventoryItem,
} from "../types";
import "./css/pageHome.css";
import CommonPage from "../components/commonPage";
import { useApi } from "../logic/classApi";
import { useOrderApi } from "../logic/orderApi";
import { useChemicalApi } from "../logic/chemicalApi";
import {
  compareISODatetime,
  displayDateFromYmd,
  displayDatetimeString,
  formatYmdString,
  getCurrentBusinessYear,
} from "../logic/dateUtils";
import { useChemicalVolumeApi } from "../logic/chemicalVolumeApi";
import { useProcessPlanApi } from "../logic/processPlanApi";
import { useDownloadApi } from "../logic/downloadApi";
import Loading from "../components/loading";
import { PAGE, usePermission } from "../logic/permission";
import useCookie from "../logic/cookie";
import InputNumText from "../components/inputNumText";
import { showErrorMessage } from "../components/appPane";
import { MESSAGES } from "../constants/const";
import { BatchOrderModal } from "../components/modal/modalInventoryBatchOrder";

/**
 * ホーム画面
 * @param props
 * @returns
 */
const Home: React.FC<CommonPageProps> = ({
  showHeader,
  showSidebar,
  onOpenSubWindow,
  onLogout,
}) => {
  // 表示権限チェック
  const [permission] = usePermission(PAGE.HOME);
  if (!permission.canDisplay) {
    // 表示権限がない場合は、表示可能画面に自動遷移させるため、ローディング表示のみを返す
    return <Loading />;
  }
  // 支所コード
  const [cookieData] = useCookie();
  const branchCode = cookieData.branchCode;
  const furnaces = cookieData.branchFurnaces;

  // API呼び出し
  const api = useApi();
  const [orderData, orderHandle] = useOrderApi(api, branchCode);
  const [chemicalData] = useChemicalApi(api, branchCode);
  const [volumeData, volumeHandle] = useChemicalVolumeApi(api, branchCode);
  const [, processPlanHandle] = useProcessPlanApi(api, branchCode, furnaces);
  const [downloadHandle] = useDownloadApi(api, branchCode);

  // 在庫残量情報
  const [inventoryGroups, setInventoryGroups] = useState<InventoryGroup[]>([]);
  // 納品予定情報
  const [deliverySchedules, setDeliverySchedules] = useState<DeliverySchedule[]>([]);
  // 当年
  const currentYear = getCurrentBusinessYear();
  // 指定年度
  const [year, setYear] = useState(currentYear);
  // 入力中の締め日
  const [eoms, setEoms] = useState([]);
  // 締め日入力用の年月リスト
  const yms = useMemo(
    () =>
      new Array(13)
        .fill("")
        .map(
          (_, index) =>
            `${year + Math.floor((index + 2) / 12)}${`0${index === 9 ? 12 : (index + 3) % 12}`.slice(-2)}`
        ),
    [year]
  );
  const [showBatchOrder, setShowBatchOrder] = useState(false);

  // 非同期処理中フラグ
  const [isProc, setProc] = useState(false);
  // 非同期処理実行
  const doProc = async (fn: () => Promise<void>) => {
    if (isProc) return;
    setProc(true);
    try {
      await fn();
    } finally {
      setProc(false);
    }
  };

  // ポップアップキャンセル時処理
  const handleCancel = () => {
    setShowBatchOrder(false);
  };

  // 納入予定実績データ作成
  const createDeliverySchedule = () => {
    const schedules = orderData.orders
      .sort((a, b) => compareISODatetime(a.orderDatetime, b.orderDatetime))
      .filter(o => chemicalData.isValidChemical(o.chemicalId))
      .map(o => {
        const chemical = chemicalData.getChemical(o.chemicalId);
        const [planDate, planTime] = displayDatetimeString(o.orderDatetime);
        const [planDateEnd, planTimeEnd] = displayDatetimeString(o.orderDatetimeEnd);
        const [resultDate, resultTime] = displayDatetimeString(o.orderResultdatetime);
        // 確認の遅延判定は日付比較、実績入力の遅延判定は日時比較
        const isDelayedDate =
          compareISODatetime(displayDatetimeString(new Date().toISOString())[0], planDate) > 0;
        const isDelayedTime = compareISODatetime(new Date().toISOString(), o.orderDatetime) > 0;
        const isApproved = Boolean(o.hiddenAt);

        return {
          chemicalId: o.chemicalId,
          orderAdddatetime: o.orderAdddatetime,
          chemicalName: chemical?.chemicalName ?? "",
          planDate,
          planTime,
          planTimeEnd,
          planVolume: o.orderVolume,
          planUnit: chemical?.chemicalUnit2 || chemical?.chemicalUnit1 || "",
          resultDate,
          resultTime,
          resultVolume: o.orderResultvolume,
          isDelayedDate,
          isDelayedTime,
          isApproved,
        } as DeliverySchedule;
      });
    setDeliverySchedules(schedules);
  };

  // 在庫残量データ作成
  const createInventryDatas = () => {
    // 薬剤分類と薬剤販売業者
    const chemicals = chemicalData.getValidChemicals();
    const chemicalTypes = Array.from(new Set(chemicals.map(c => c.chemicalTypeId))).sort();
    const inventoryGroups: InventoryGroup[] = chemicalTypes.map(typeId => {
      const chemicalType = chemicalData.getType(typeId);
      const items: InventoryItem[] = chemicals
        .filter(c => c.chemicalTypeId === typeId)
        .sort((a, b) => a.chemicalId - b.chemicalId)
        .map(c => {
          const volume = volumeData.getLastVolume(c.chemicalId);
          const order = orderData.orders?.find(o => o.chemicalId === c.chemicalId);
          return {
            id: c.chemicalId,
            name: c.chemicalName,
            type: c.chemicalViewtype,
            volume: volume?.volumeData,
            stockVolumes: volume?.stockVolumeData,
            date: volume?.volumeDate,
            limit: c.chemicalLimit,
            stockLimits: c.chemicalStockLimit,
            warningThreshold: c.chemicalBorder1,
            dangerThreshold: c.chemicalBorder2,
            warningStockThreshold: c.chemicalStockBorder1,
            dangerStockThreshold: c.chemicalStockBorder2,
            unit: c.chemicalUnit1,
            unit2: c.chemicalUnit2,
            unit2Calc: c.chemicalUnit2Calc,
            isOrdered: !!order,
            storageCount: c.chemicalStorageCount,
            iconImage1: c.chemicalIcon1,
            iconImage2: c.chemicalIcon2,
            volumeOpenedBoxData: volume?.volumeOpenedBoxData,
            countEmptyBox: volume?.countEmptyBox,
          };
        });
      return {
        id: chemicalType.chemicalTypeId,
        name: chemicalType.chemicalTypeName,
        items,
      };
    });

    setInventoryGroups(inventoryGroups);
  };

  // 発注レベル以下（未発注）リストをinventoryGroupsから抽出
  const dangerLevelList: InventoryGroup[] = inventoryGroups.map(group => {
    return {
      ...group,
      items: group.items.filter(item => item.volume <= item.dangerThreshold && !item.isOrdered),
    };
  });

  // 補給レベル以下（未発注）リストをinventoryGroupsから抽出
  const warningLevelList: InventoryGroup[] = inventoryGroups.map(group => {
    return {
      ...group,
      items: group.items.filter(
        item =>
          ((item.volume > item.dangerThreshold && item.volume <= item.warningThreshold) ||
            item.stockVolumes?.find(
              (volume, index) => volume <= item.warningStockThreshold[index]
            )) &&
          !item.isOrdered
      ),
    };
  });

  // 発注済みリストをinventoryGroupsから抽出
  const isOrderedList: InventoryGroup[] = inventoryGroups.map(group => {
    return {
      ...group,
      items: group.items.filter(item => item.isOrdered),
    };
  });
  // 発注レベル（未発注）、補給レベル（未発注）、発注済み以外のリストをinventoryGroupsから抽出
  const inStockList: InventoryGroup[] = inventoryGroups.map(group => {
    return {
      ...group,
      items: group.items.filter(item => {
        const isInStock =
          item.volume > item.dangerThreshold &&
          item.volume > item.warningThreshold &&
          !item.stockVolumes?.find((volume, index) => volume < item.warningStockThreshold[index]) &&
          !item.isOrdered;

        return isInStock;
      }),
    };
  });

  // 表示用のリストを作成
  const inventoryGroupList = [
    { title: "発注レベル以下（未発注）", list: dangerLevelList },
    { title: "補給レベル以下（未発注）", list: warningLevelList },
    { title: "カテゴリ別", list: inStockList },
    { title: "発注済み", list: isOrderedList },
  ];

  // 納入実績登録
  const handleDeliveryScheduleUpdate = async (
    schedule: DeliverySchedule,
    date: string,
    time: string,
    volume: number
  ) => {
    await doProc(async () => {
      if (!(permission.canEdit || permission.canEditOrderResult)) return;
      await orderHandle.updateOrderResult(
        schedule.chemicalId,
        schedule.orderAdddatetime,
        date,
        time,
        volume
      );
    });
  };

  // 納入実績確認
  const handleDeliveryScheduleApprove = async (schedule: DeliverySchedule) => {
    await doProc(async () => {
      if (!permission.canEdit) return;
      await orderHandle.approveOrderResult(schedule.chemicalId, schedule.orderAdddatetime);
    });
  };

  // 画面初期表示時
  useEffect(() => {
    volumeHandle.loadLastVolumes();
    orderHandle.loadUnapprovedOrders();
  }, []);

  // 納品予定取得完了時
  useEffect(() => {
    if (orderData.orders && chemicalData.isLoaded) {
      createDeliverySchedule();
    }
  }, [orderData.orders, chemicalData.isLoaded]);

  // 薬剤在庫取得完了時
  useEffect(() => {
    if (volumeData.volumes && orderData.orders && chemicalData.isLoaded) {
      createInventryDatas();
    }
  }, [volumeData.volumes, orderData.orders, chemicalData.isLoaded]);

  // 在庫残量実績登録
  const handleSaveChemicalVolume = async (record: UsageRecord) => {
    await doProc(async () => {
      if (!permission.canEdit) return;
      await volumeHandle.saveVolume?.(
        record.itemId,
        record.date,
        record.actualUsage,
        record.actualTankUsages,
        record.openedActualUsage,
        record.emptyBoxNum
      );
    });
  };

  // 在庫残量 図クリック
  const handleItemClick = (itemId: number) => {
    // ホーム画面または発注画面の権限が入力可の場合は推移表示・発注画面へ遷移可能
    if (permission.canEdit || permission.canDisplayOrder) {
      // 別ウィンドウで推移グラフを表示
      const subWindow = window.open(`/home/${itemId}`, itemId.toString(), "width=800,height=800");
      onOpenSubWindow(subWindow);
    }
  };

  // 締め日用年度選択時
  useEffect(() => {
    orderHandle.loadOrderEoms(`${year}03`, `${year + 1}03`);
  }, [year]);

  // 締め日情報取得時
  useEffect(() => {
    if (orderData.eoms)
      setEoms(
        yms.map(ym => {
          const eom = orderData.eoms.find(e => e.eomYm === ym);
          return eom ? Number(eom.eomDate).toString() : "";
        })
      );
  }, [orderData.eoms]);

  // 締め日登録ボタン
  const handleEomRegister = async () => {
    if (!permission.canEdit) return;
    if (eoms.includes("")) {
      showErrorMessage(MESSAGES.ERROR_INPUT_EMPTY);
      return;
    }
    const values = yms.map((ym, index) => `${ym}${`0${eoms[index]}`.slice(-2)}`);
    if (
      values.find(v => {
        const date = new Date(displayDateFromYmd(v));
        if (Number.isNaN(date.getTime())) return true;
        if (formatYmdString(date) !== v) return true;
        return false;
      })
    ) {
      showErrorMessage(MESSAGES.ERROR_INPUT_INVALID);
      return;
    }
    await orderHandle.updateEoms(values);
    await orderHandle.loadOrderEoms(`${year}03`, `${year + 1}03`);
  };

  // ファイルアップロード用INPUTタグ
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 焼却量計画アップロードボタン押下時
  const onFileInput = () => {
    if (!permission.canEdit) return;
    fileInputRef.current?.click();
  };

  // 焼却量計画アップロード
  const handleProcessPlanUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!permission.canEdit) return;
    const file = event.target.files?.[0];
    if (!file) return;
    await processPlanHandle.uploadPlans(year.toString(), file);
  };

  // 納入実績表ダウンロード
  const handleInventoryExport = async () => {
    const currentEoms = orderData.eoms.filter(
      e => Number(e.eomYm) >= Number(`${year}03`) && Number(e.eomYm) <= Number(`${year + 1}03`)
    );
    if (currentEoms.length < 13) {
      showErrorMessage(MESSAGES.ERROR_ORDER_RESULT_DOWNLOAD);
      return;
    }
    const fileName = `納入実績表_${year}.xlsx`;
    downloadHandle.downloadOrderResult(fileName, year.toString());
  };

  // 年間調達計画・実績表ダウンロード
  const handleUsageReportExport = async () => {
    const fileName = `PD-8-3,4_年間・月間調達計画表／実績表(可燃ごみ処理施設)_${year}.xlsx`;
    downloadHandle.downloadOrderPlan(fileName, year.toString());
  };

  // 一括発注開始
  const startBatchOrder = () => {
    if (!permission.canEdit) return;
    setShowBatchOrder(true);
  };

  return (
    <CommonPage showHeader={showHeader} showSidebar={showSidebar} onLogout={onLogout}>
      <div className="app-main home">
        {/* 在庫残量表示 */}
        <section className="inventory-section">
          <div className="inventory-section-header">
            <h3>残量</h3>
            <button className="positive" onClick={startBatchOrder}>
              一括発注
            </button>
            {showBatchOrder && <BatchOrderModal onClose={handleCancel} />}
          </div>
          {inventoryGroupList.map(group => (
            <InventoryDisplay
              groupTitle={group.title}
              groups={group.list}
              onItemClick={handleItemClick}
              onSave={handleSaveChemicalVolume}
              canEdit={permission.canEdit}
              canOrder={permission.canDisplayOrder}
            />
          ))}
        </section>

        {/* 納品予定表 */}
        <section className="delivery-section">
          <h3>本日の納入予定</h3>
          <DeliveryTable
            isToday={true}
            schedules={deliverySchedules}
            onResultInput={handleDeliveryScheduleUpdate}
            onApprove={handleDeliveryScheduleApprove}
            canEdit={permission.canEdit || permission.canEditOrder || permission.canEditOrderResult}
            canCommit={permission.canEdit || permission.canEditOrder}
          />
          <hr></hr>
          <h3>明日以降の納入予定</h3>
          <DeliveryTable
            isToday={false}
            schedules={deliverySchedules}
            onResultInput={handleDeliveryScheduleUpdate}
            onApprove={handleDeliveryScheduleApprove}
            canEdit={permission.canEdit || permission.canEditOrder || permission.canEditOrderResult}
            canCommit={permission.canEdit || permission.canEditOrder}
          />
        </section>

        {/* 年度選択 */}
        <section className="download-section">
          <h3>提出書類出力</h3>
          <div className="parts">
            <label>納入締め日(指定年度): </label>
            <select id="fiscalYear" value={year} onChange={e => setYear(Number(e.target.value))}>
              {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                <option key={y} value={y}>
                  {y}年度
                </option>
              ))}
            </select>

            {permission.canEdit && (
              <button
                className="positive"
                onClick={handleEomRegister}
                disabled={eoms.find(e => e === "")}
              >
                納入締め日登録
              </button>
            )}
          </div>

          <table>
            <thead>
              <tr>
                <th colSpan={1}>前年度</th>
                <th colSpan={12}>指定年度</th>
              </tr>
              <tr>
                {yms.map(ym => (
                  <th key={ym}>{`${Number(ym.slice(-2))}月`}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {yms.map((ym, index) => (
                  <td key={ym}>
                    <InputNumText
                      val={eoms[index]}
                      len={2}
                      onChange={v =>
                        setEoms([...eoms.slice(0, index), v, ...eoms.slice(index + 1)])
                      }
                      disabled={!permission.canEdit}
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>

          {/* 焼却量計画データボタン */}
          <div className="parts">
            {permission.canEdit && (
              <>
                <button className="positive" onClick={onFileInput}>
                  計画焼却量データをアップロード
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleProcessPlanUpload}
                  accept=".xlsx,.xls,.csv"
                  style={{ display: "none" }}
                />
              </>
            )}

            <button className="positive" onClick={handleInventoryExport}>
              納入実績表をダウンロード
            </button>

            <button className="positive" onClick={handleUsageReportExport}>
              年間調達計画・実績表をダウンロード
            </button>
          </div>
        </section>
      </div>
    </CommonPage>
  );
};

export default Home;
